import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  LinearProgress,
  Collapse,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Info,
  ExpandMore,
  ExpandLess,
  Assignment,
  Description,
  Architecture,
  Map,
  Engineering,
  InsertDriveFile,
  Edit,
  AutoAwesome,
} from '@mui/icons-material';
import { Document, Requirement } from '../types/project';
import { projectsApi } from '../services/api';
import Pagination from './Pagination';

// Evidence viewer components
import EvidencePageChips from './evidence/EvidencePageChips';
import EvidencePageViewer from './evidence/EvidencePageViewer';
import MultiPageEvidenceViewer from './evidence/MultiPageEvidenceViewer';

interface ComplianceReviewProps {
  projectId: string;
  requirements: Requirement[];
  submissionDocuments: Document[];
  onComplianceUpdate: (requirementId: string, status: string, notes?: string) => Promise<void>;
  onRequirementsRefresh: () => Promise<void>;
}


const getDocumentIcon = (filename: string | undefined, documentType?: string) => {
  if (!filename) {
    return <InsertDriveFile sx={{ color: 'text.secondary' }} />;
  }

  const extension = filename.split('.').pop()?.toLowerCase();
  const name = filename.toLowerCase();

  // Check for specific submission document types
  if (documentType === 'submission') {
    if (name.includes('plan') || name.includes('drawing') || extension === 'dwg') {
      return <Architecture sx={{ color: 'primary.main' }} />;
    }
    if (name.includes('survey') || name.includes('site') || name.includes('boundary')) {
      return <Map sx={{ color: 'success.main' }} />;
    }
    if (name.includes('calculation') || name.includes('calc') || name.includes('analysis')) {
      return <Engineering sx={{ color: 'warning.main' }} />;
    }
  }

  switch (extension) {
    case 'pdf':
      return <Description sx={{ color: 'secondary.main' }} />;
    case 'dwg':
    case 'dxf':
      return <Architecture sx={{ color: 'primary.main' }} />;
    default:
      return <InsertDriveFile sx={{ color: 'text.secondary' }} />;
  }
};

const getComplianceStatusColor = (status?: string) => {
  switch (status) {
    case 'compliant':
      return 'success';
    case 'non_compliant':
    case 'non-compliant':  // Handle both formats
      return 'error';
    case 'unclear':
    case 'pending':  // Handle both formats
      return 'warning';
    case 'not_applicable':
    case 'not applicable':  // Handle both formats
    case 'not-applicable':
      return 'info';
    default:
      return 'default';
  }
};

const getComplianceStatusIcon = (status?: string) => {
  switch (status) {
    case 'compliant':
      return <CheckCircle sx={{ color: 'success.main' }} />;
    case 'non_compliant':
    case 'non-compliant':  // Handle both formats
      return <ErrorIcon sx={{ color: 'error.main' }} />;
    case 'unclear':
    case 'pending':  // Handle both formats
      return <Warning sx={{ color: 'warning.main' }} />;
    case 'not_applicable':
    case 'not applicable':  // Handle both formats
    case 'not-applicable':
      return <Info sx={{ color: 'info.main' }} />;
    default:
      return <Assignment sx={{ color: 'text.secondary' }} />;
  }
};

export default function ComplianceReview({
  projectId,
  requirements,
  submissionDocuments,
  onComplianceUpdate,
  onRequirementsRefresh
}: ComplianceReviewProps) {
  // Simplified state - only what we actually need
  const [expandedRequirement, setExpandedRequirement] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterOrdinanceHierarchy, setFilterOrdinanceHierarchy] = useState<string>('all');
  const [filterOrdinanceType, setFilterOrdinanceType] = useState<string>('all');

  // Pagination state
  const [paginationState, setPaginationState] = useState({
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    showingStart: 0,
    showingEnd: 0,
    loading: false
  });

  // Local requirements state for paginated data
  const [paginatedRequirements, setPaginatedRequirements] = useState<any[]>([]);

  // State for all approved requirements (for statistics)
  const [allApprovedRequirements, setAllApprovedRequirements] = useState<any[]>([]);

  // AI Evaluation state
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluationProgress, setEvaluationProgress] = useState<{
    current: number;
    total: number;
    message: string;
    phase?: string;
  } | null>(null);
  const [evaluationJobId, setEvaluationJobId] = useState<string | null>(null);
  const [evaluationStartTime, setEvaluationStartTime] = useState<Date | null>(null);
  const [evaluationCompleted, setEvaluationCompleted] = useState(false);
  const [evaluationError, setEvaluationError] = useState<string | null>(null);

  // AI Rerun state
  const [isRerunning, setIsRerunning] = useState(false);
  const [rerunJobId, setRerunJobId] = useState<string | null>(null);
  const [rerunStartTime, setRerunStartTime] = useState<Date | null>(null);

  // Report generation state
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Evidence viewer state
  const [evidenceViewerOpen, setEvidenceViewerOpen] = useState(false);
  const [multiPageViewerOpen, setMultiPageViewerOpen] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState<any | null>(null);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [selectedDocument, setSelectedDocument] = useState<{id: string; name: string} | null>(null);

  // Add function to fetch all approved requirements for statistics
  const fetchAllApprovedRequirements = async () => {
    try {
      let allRequirements: any[] = [];
      let currentPage = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await projectsApi.getRequirements(
          projectId,
          currentPage,
          100, // Maximum allowed per page
          'all',
          'all',
          'all', // requirement_type_filter
          'all', // zone_filter
          '',    // searchTerm
          'all', // ordinance_type_filter
          'all'  // ordinance_hierarchy_filter
        );

        if (response && response.requirements && Array.isArray(response.requirements)) {
          const formattedRequirements = response.requirements.map((req: any) => ({
            id: req.id,
            compliance_status: req.compliance_status || null,
            approved: req.approved || req.validation_status === "approved" || false,
          }));

          allRequirements = [...allRequirements, ...formattedRequirements];

          // Check if we have more pages
          if (response.pagination && response.pagination.has_next) {
            currentPage++;
          } else {
            hasMorePages = false;
          }
        } else {
          hasMorePages = false;
        }
      }

      setAllApprovedRequirements(allRequirements);
    } catch (error) {
      console.error('Failed to fetch all approved requirements:', error);
    }
  };

  // Add function to fetch paginated requirements
  const fetchPaginatedRequirements = async (
    page: number = paginationState.currentPage,
    limit: number = paginationState.pageSize,
    statusFilter: string = filterStatus,
    categoryFilter: string = filterCategory
  ) => {
    setPaginationState(prev => ({ ...prev, loading: true }));

    try {
      const response = await projectsApi.getRequirements(
        projectId,
        page,
        limit,
        statusFilter,
        categoryFilter,
        'all', // requirement_type_filter
        'all', // zone_filter
        '',    // searchTerm
        filterOrdinanceType,
        filterOrdinanceHierarchy
      );

      if (response && response.requirements && Array.isArray(response.requirements)) {
        const formattedRequirements = response.requirements.map((req: any) => ({
          id: req.id,
          title: req.title,
          requirement_type: req.requirement_type || 'Other',
          category: req.category || 'General',
          description: req.description,
          numeric_value: req.measurement_value || null,
          unit: req.unit,
          source_document_name: req.source_document_name || null,
          source_section: req.source_section || null,
          source_page: req.source_page_start || 1,
          source_text: req.description,
          confidence: req.confidence_score,
          district: Array.isArray(req.zoning_districts) ? req.zoning_districts.join(', ') : '',
          zoning_districts: req.zoning_districts || [],
          zoning_district_names: req.zoning_district_names || {},
          applies_to: Array.isArray(req.applies_to) ? req.applies_to.join(', ') : '',
          measurement_method: req.measurement_method,
          tolerance: req.tolerance,
          confidence_score: req.confidence_score,
          approved: req.approved || req.validation_status === "approved" || false,
          compliance_status: req.compliance_status || null,
          compliance_notes: req.compliance_notes || null,
          compliance_confidence: req.compliance_confidence || null,
          evidence_documents: req.evidence_documents || [],
          compliance_evaluation: req.compliance_evaluation || null
        }));

        setPaginatedRequirements(formattedRequirements);

        // Update pagination state
        if (response.pagination) {
          setPaginationState(prev => ({
            ...prev,
            currentPage: response.pagination.current_page,
            pageSize: response.pagination.page_size,
            totalCount: response.pagination.total_count,
            totalPages: response.pagination.total_pages,
            showingStart: response.pagination.showing_start,
            showingEnd: response.pagination.showing_end,
            loading: false
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch paginated requirements:', error);
      setPaginationState(prev => ({ ...prev, loading: false }));
    }
  };

  // Calculate compliance statistics from ALL approved requirements (not just current page)
  const approvedRequirementsForStats = allApprovedRequirements.filter(req => req.approved === true);
  const complianceStats = approvedRequirementsForStats.reduce((acc, req) => {
    const status = req.compliance_status || 'pending';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const compliantCount = complianceStats.compliant || 0;
  const nonCompliantCount = complianceStats['non-compliant'] || 0;  // Use hyphen format from database
  const unclearCount = complianceStats.unclear || 0;  // Use 'unclear' format from database
  const applicableRequirementsCount = compliantCount + nonCompliantCount + unclearCount;
  const compliancePercentage = applicableRequirementsCount > 0 ? (compliantCount / applicableRequirementsCount) * 100 : 0;

  // Rerun options logic
  const hasNonCompliant = nonCompliantCount > 0;
  const hasUnclear = unclearCount > 0;
  const showRerunOptions = (hasNonCompliant || hasUnclear) && submissionDocuments.length > 0 && !isEvaluating && !isRerunning;

  // Use the original requirements prop for checking if we have approved requirements and categories
  const approvedRequirements = requirements.filter(req => req.approved === true);
  const categories = Array.from(new Set(approvedRequirements.map(req => req.category)));

  const handleComplianceChange = async (requirementId: string, newStatus: string, notes?: string) => {
    try {
      await onComplianceUpdate(requirementId, newStatus, notes);

      // Refresh the paginated data to show the updated compliance status
      await fetchPaginatedRequirements();
      await fetchAllApprovedRequirements();
    } catch (error) {
      console.error('Failed to update compliance status:', error);
    }
  };

  const handleExpandToggle = (requirementId: string) => {
    setExpandedRequirement(expandedRequirement === requirementId ? null : requirementId);
  };

  // Evidence viewer handlers
  const handlePageClick = (requirement: any, documentId: string, pageNumber: number, documentName: string) => {
    setSelectedRequirement(requirement);
    setSelectedPage(pageNumber);
    setSelectedDocument({ id: documentId, name: documentName });
    setEvidenceViewerOpen(true);
  };

  const handleViewAllClick = (requirement: any) => {
    setSelectedRequirement(requirement);
    setMultiPageViewerOpen(true);
  };

  const handleEvidenceViewerClose = () => {
    setEvidenceViewerOpen(false);
    setSelectedRequirement(null);
  };

  const handleMultiPageViewerClose = () => {
    setMultiPageViewerOpen(false);
    setSelectedRequirement(null);
  };

  const handleEvidencePageChange = (page: number) => {
    setSelectedPage(page);
  };

  const handleMultiPageClick = (page: number) => {
    setSelectedPage(page);
    setMultiPageViewerOpen(false);
    setEvidenceViewerOpen(true);
  };

  // Report generation handler
  const handleGenerateReport = async () => {
    try {
      setIsGeneratingReport(true);

      // Call API to generate report
      const reportBlob = await projectsApi.generateComplianceReport(projectId);

      // Create download link
      const url = window.URL.createObjectURL(reportBlob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with project name and date
      const currentDate = new Date().toISOString().split('T')[0].replace(/-/g, '');
      const safeProjectName = (paginatedRequirements[0]?.requirement?.project?.name || 'Project')
        .replace(/[^a-zA-Z0-9\s\-_]/g, '')
        .replace(/\s+/g, '_');

      link.download = `Compliance_Report_${safeProjectName}_${currentDate}.pdf`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ Successfully generated and downloaded compliance report');

    } catch (error) {
      console.error('❌ Failed to generate compliance report:', error);

      // Show user-friendly error
      if (error instanceof Error) {
        alert(`Failed to generate report: ${error.message}`);
      } else {
        alert('Failed to generate compliance report. Please try again.');
      }
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    fetchPaginatedRequirements(page, paginationState.pageSize, filterStatus, filterCategory);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPaginationState(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
    fetchPaginatedRequirements(1, newPageSize, filterStatus, filterCategory);
  };

  const handleFilterChange = (newStatusFilter?: string, newCategoryFilter?: string) => {
    const statusFilter = newStatusFilter !== undefined ? newStatusFilter : filterStatus;
    const categoryFilter = newCategoryFilter !== undefined ? newCategoryFilter : filterCategory;

    setPaginationState(prev => ({ ...prev, currentPage: 1 }));
    fetchPaginatedRequirements(1, paginationState.pageSize, statusFilter, categoryFilter);
  };

  // Load initial data when component mounts or when approved requirements change
  useEffect(() => {
    if (approvedRequirements.length > 0) {
      // Fetch all approved requirements for statistics
      fetchAllApprovedRequirements();
      // Fetch first page of paginated requirements
      fetchPaginatedRequirements(1, 20, 'all', 'all');
    }
  }, [projectId, approvedRequirements.length]);

  // Check if any approved requirements need compliance analysis
  const needsAnalysis = approvedRequirements.some(req => !req.compliance_evaluation) && submissionDocuments.length > 0 && !evaluationCompleted;

  // Handle starting AI evaluation
  const handleStartAnalysis = async () => {
    try {
      setIsEvaluating(true);
      setEvaluationStartTime(new Date());
      setEvaluationError(null);
      setEvaluationCompleted(false);
      setEvaluationProgress({ current: 0, total: 1, message: "Starting AI compliance evaluation...", phase: "preparation" });

      // Get submission document IDs
      const submissionDocIds = submissionDocuments.map(doc => doc.id);

      // Start compliance evaluation
      const result = await projectsApi.startComplianceEvaluation(projectId, submissionDocIds);

      if (!result.job_id) {
        const errorMsg = 'No job ID returned from compliance evaluation';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      setEvaluationJobId(result.job_id);

      // Start polling for progress (no frontend timeout - trust backend job management)
      pollEvaluationProgress(result.job_id);

    } catch (error: any) {
      console.error('Failed to start AI evaluation:', error);
      setIsEvaluating(false);
      setEvaluationStartTime(null);
      setEvaluationProgress(null);
      setEvaluationJobId(null);

      // Extract proper error message from API response
      let errorMessage = 'Unknown error';
      if (error?.response?.data?.detail) {
        // FastAPI error format
        errorMessage = error.response.data.detail;
      } else if (error?.message) {
        // Fallback to generic error message
        errorMessage = error.message;
      }

      setEvaluationError(`Failed to start evaluation: ${errorMessage}`);
    }
  };

  // Handle starting AI rerun
  const handleStartRerun = async (rerunStatuses: string[]) => {
    try {
      setIsRerunning(true);
      setRerunStartTime(new Date());
      setEvaluationError(null);
      setEvaluationCompleted(false);
      setEvaluationProgress({ current: 0, total: 1, message: "Starting AI compliance rerun...", phase: "preparation" });

      // Get submission document IDs
      const submissionDocIds = submissionDocuments.map(doc => doc.id);

      // Start compliance rerun
      const result = await projectsApi.startComplianceRerun(projectId, submissionDocIds, rerunStatuses);

      if (!result.job_id) {
        const errorMsg = 'No job ID returned from compliance rerun';
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      setRerunJobId(result.job_id);
      setEvaluationJobId(result.job_id); // Use the same job tracking

      // Start polling for progress (reuse existing progress tracking)
      pollEvaluationProgress(result.job_id);

    } catch (error: any) {
      console.error('Failed to start AI rerun:', error);
      setIsRerunning(false);
      setRerunStartTime(null);
      setEvaluationProgress(null);
      setRerunJobId(null);

      // Extract proper error message from API response
      let errorMessage = 'Unknown error';
      if (error?.response?.data?.detail) {
        // FastAPI error format
        errorMessage = error.response.data.detail;
      } else if (error?.message) {
        // Fallback to generic error message
        errorMessage = error.message;
      }

      setEvaluationError(`Failed to start rerun: ${errorMessage}`);
    }
  };

  const pollEvaluationProgress = async (jobId: string, pollCount: number = 0) => {
    try {
      const status = await projectsApi.getJobStatus(jobId);

      // Enhanced progress messages based on elapsed time
      const elapsedMinutes = evaluationStartTime
        ? Math.floor((Date.now() - evaluationStartTime.getTime()) / 60000)
        : 0;

      let enhancedMessage = status.progress_message || "Processing...";
      if (elapsedMinutes > 20 && status.status === 'processing') {
        enhancedMessage += ` (${elapsedMinutes} minutes elapsed - complex evaluations can take 60+ minutes)`;
      }

      setEvaluationProgress({
        current: status.progress_current || 0,
        total: status.progress_total || 100,
        message: enhancedMessage,
        phase: status.progress_current <= 100 ? "preparation" : "requirements"
      });

      if (status.status === 'completed') {
        setIsEvaluating(false);
        setIsRerunning(false); // Reset rerun state too
        setEvaluationStartTime(null);
        setRerunStartTime(null); // Reset rerun timing
        setEvaluationProgress(null);
        setEvaluationError(null);
        setEvaluationCompleted(true);

        // Refresh requirements data to get updated compliance statuses
        await onRequirementsRefresh();
        // Also refresh statistics and current page
        fetchAllApprovedRequirements();
        fetchPaginatedRequirements();
      } else if (status.status === 'failed' || status.status === 'cancelled') {
        setIsEvaluating(false);
        setIsRerunning(false); // Reset rerun state too
        setEvaluationStartTime(null);
        setRerunStartTime(null); // Reset rerun timing
        setEvaluationProgress(null);

        const errorMessage = status.status === 'cancelled'
          ? `Evaluation cancelled: ${status.progress_message || 'Job was cancelled by the system'}`
          : `Evaluation failed: ${status.progress_message || 'Unknown error'}`;

        setEvaluationError(errorMessage);
        console.error('AI evaluation ended:', status.status, status.progress_message);
      } else {
        // Continue polling with progressive backoff: 2s -> 5s -> 10s -> 15s -> 30s (max)
        const delays = [2000, 5000, 10000, 15000, 30000];
        const delay = delays[Math.min(pollCount, delays.length - 1)];

        setTimeout(() => pollEvaluationProgress(jobId, pollCount + 1), delay);
      }

    } catch (error) {
      console.error('Error polling evaluation progress:', error);
      setIsEvaluating(false);
      setIsRerunning(false); // Reset rerun state too
      setEvaluationStartTime(null);
      setRerunStartTime(null); // Reset rerun timing
      setEvaluationProgress(null);

      const errorMessage = error instanceof Error ? error.message : 'Network error';
      setEvaluationError(`Polling error: ${errorMessage}`);
    }
  };

  // Load recent compliance job and restore state on component mount
  useEffect(() => {
    const loadRecentJob = async () => {
      try {
        const jobsData = await projectsApi.getComplianceJobs(projectId);
        const allJobs = jobsData.compliance_jobs;

        if (allJobs.length > 0) {
          // First priority: Check for running jobs and restore polling state
          const runningJob = allJobs.find((job: any) =>
            job.status === 'processing' || job.status === 'pending'
          );

          if (runningJob) {
            setEvaluationJobId(runningJob.job_id);
            setIsEvaluating(true);
            setEvaluationStartTime(new Date()); // Set timeout reference
            setEvaluationError(null); // Clear any previous errors
            setEvaluationProgress({
              current: runningJob.progress_current || 0,
              total: runningJob.progress_total || 100,
              message: runningJob.progress_message || "Processing...",
              phase: runningJob.progress_current <= 100 ? "preparation" : "requirements"
            });
            // Resume polling for this running job
            pollEvaluationProgress(runningJob.job_id);
            return;
          }

          // Second priority: Load most recent completed job and mark as completed
          const completedJobs = allJobs.filter((job: any) => job.status === 'completed');
          if (completedJobs.length > 0) {
            const mostRecentJob = completedJobs[0]; // Already sorted by created_at desc
            setEvaluationJobId(mostRecentJob.job_id);
            setEvaluationCompleted(true); // Mark evaluation as completed
          }
        }
      } catch (error) {
        console.error('Failed to load recent compliance jobs:', error);
      }
    };

    if (!evaluationJobId) {
      loadRecentJob();
    }
  }, [projectId, evaluationJobId]);

  // Reset evaluation state when submission documents change
  useEffect(() => {
    // Reset evaluation completed state when documents change
    // This ensures the "Run AI Analysis" button appears when new documents are uploaded
    if (evaluationCompleted && submissionDocuments.length > 0) {
      setEvaluationCompleted(false);
    }
  }, [submissionDocuments.length, evaluationCompleted]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Compliance Review
      </Typography>

      {/* Analysis Error */}
      {evaluationError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setEvaluationError(null)}>
          {evaluationError}
        </Alert>
      )}

      {/* AI Evaluation Progress */}
      {(isEvaluating || isRerunning) && evaluationProgress && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {evaluationProgress.message}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={evaluationProgress.phase === "preparation"
              ? evaluationProgress.current
              : evaluationProgress.total > 0
                ? (evaluationProgress.current / evaluationProgress.total) * 100
                : 0
            }
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {evaluationProgress.phase === "preparation"
              ? `Preparation: ${evaluationProgress.current}%`
              : evaluationProgress.phase === "detailed_analysis"
                ? `Extracting detailed evidence: ${evaluationProgress.current} of ${evaluationProgress.total}`
                : `Processing requirements: ${evaluationProgress.current} of ${evaluationProgress.total}`
            }
            {evaluationStartTime && (
              <> • Running for {Math.floor((Date.now() - evaluationStartTime.getTime()) / 1000 / 60)} minutes</>
            )}
          </Typography>
        </Box>
      )}

      {/* Run AI Analysis Section */}
      {needsAnalysis && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<AutoAwesome />}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ mr: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Ready for AI Compliance Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                {submissionDocuments.length} submission document{submissionDocuments.length !== 1 ? 's' : ''} ready to be analyzed against {approvedRequirements.length} approved requirement{approvedRequirements.length !== 1 ? 's' : ''}.
                {submissionDocuments.length === 1 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                    ({submissionDocuments[0].name})
                  </Typography>
                )}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={isEvaluating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
              onClick={handleStartAnalysis}
              disabled={isEvaluating}
              sx={{
                mt: { xs: 2, sm: 0 },
                fontWeight: 600,
                minWidth: 200
              }}
            >
              {isEvaluating ? 'Running Analysis...' : 'Run AI Analysis'}
            </Button>
          </Box>
        </Alert>
      )}

      {/* Rerun AI Analysis Section */}
      {showRerunOptions && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<AutoAwesome />}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ mr: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Rerun AI Analysis for Specific Results
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                {hasNonCompliant && hasUnclear && (
                  <>Found {nonCompliantCount} non-compliant and {unclearCount} unclear requirements that can be rerun.</>
                )}
                {hasNonCompliant && !hasUnclear && (
                  <>Found {nonCompliantCount} non-compliant requirement{nonCompliantCount !== 1 ? 's' : ''} that can be rerun.</>
                )}
                {!hasNonCompliant && hasUnclear && (
                  <>Found {unclearCount} unclear requirement{unclearCount !== 1 ? 's' : ''} that can be rerun.</>
                )}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: { xs: 2, sm: 0 } }}>
              {hasNonCompliant && (
                <Button
                  variant="outlined"
                  color="error"
                  size="large"
                  startIcon={isRerunning ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                  onClick={() => handleStartRerun(['non-compliant'])}
                  disabled={isRerunning || isEvaluating}
                  sx={{ fontWeight: 600, minWidth: 180 }}
                >
                  {isRerunning ? 'Rerunning...' : `Rerun ${nonCompliantCount} Non-Compliant`}
                </Button>
              )}
              {hasNonCompliant && hasUnclear && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="large"
                  startIcon={isRerunning ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                  onClick={() => handleStartRerun(['non-compliant', 'unclear'])}
                  disabled={isRerunning || isEvaluating}
                  sx={{ fontWeight: 600, minWidth: 200 }}
                >
                  {isRerunning ? 'Rerunning...' : `Rerun Both (${nonCompliantCount + unclearCount})`}
                </Button>
              )}
              {!hasNonCompliant && hasUnclear && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="large"
                  startIcon={isRerunning ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                  onClick={() => handleStartRerun(['unclear'])}
                  disabled={isRerunning || isEvaluating}
                  sx={{ fontWeight: 600, minWidth: 180 }}
                >
                  {isRerunning ? 'Rerunning...' : `Rerun ${unclearCount} Unclear`}
                </Button>
              )}
            </Box>
          </Box>
        </Alert>
      )}

      {/* Show message if no requirements are approved */}
      {approvedRequirements.length === 0 && requirements.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No requirements have been approved yet. Please go to the Requirements Extraction tab to approve the requirements you want to include in compliance review.
        </Alert>
      )}

      {/* Show message if no requirements exist at all */}
      {requirements.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No requirements found. Please complete the Requirements Extraction step first.
        </Alert>
      )}

      {/* Overview Statistics - Only show if there are approved requirements */}
      {approvedRequirements.length > 0 && (
        <Card elevation={0} sx={{ border: 1, borderColor: 'grey.300', mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                    {compliancePercentage.toFixed(0)}%
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Overall Compliance Rate
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={compliancePercentage}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {compliantCount} of {applicableRequirementsCount} applicable requirements marked as compliant
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                        {complianceStats.compliant || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Compliant
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.main' }}>
                        {complianceStats['non-compliant'] || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Non-Compliant
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
                        {unclearCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Unclear
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'info.main' }}>
                        {(complianceStats['not applicable'] || 0) + (complianceStats['not_applicable'] || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Not Applicable
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Document Processing Information */}
            {(() => {
              const firstRequirementWithEvidence = approvedRequirements.find(req =>
                req.evidence_documents && req.evidence_documents.length > 0 && req.compliance_evaluation
              );

              if (!firstRequirementWithEvidence ||
                  !firstRequirementWithEvidence.evidence_documents ||
                  firstRequirementWithEvidence.evidence_documents.length === 0 ||
                  !firstRequirementWithEvidence.compliance_evaluation) {
                return null;
              }

              const doc = firstRequirementWithEvidence.evidence_documents[0];
              const evaluation = firstRequirementWithEvidence.compliance_evaluation;

              return (
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'grey.300' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    {getDocumentIcon(doc.name, doc.document_type)}
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Analyzed: {doc.name}
                    </Typography>
                    {evaluation.evaluation_date && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', ml: 1 }}>
                        Processed: {new Date(evaluation.evaluation_date).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Requirements List - Show if there are approved requirements */}
      {approvedRequirements.length > 0 && (
        <>
          {/* Filters and Page Size Selector */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={filterStatus}
                label="Status Filter"
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  handleFilterChange(e.target.value, undefined);
                }}
                disabled={paginationState.loading}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="compliant">Compliant</MenuItem>
                <MenuItem value="non-compliant">Non-Compliant</MenuItem>
                <MenuItem value="unclear">Unclear</MenuItem>
                <MenuItem value="not-applicable">Not Applicable</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Category Filter</InputLabel>
              <Select
                value={filterCategory}
                label="Category Filter"
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  handleFilterChange(undefined, e.target.value);
                }}
                disabled={paginationState.loading}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Ordinance Level</InputLabel>
              <Select
                value={filterOrdinanceHierarchy}
                label="Ordinance Level"
                onChange={(e) => {
                  setFilterOrdinanceHierarchy(e.target.value);
                  fetchPaginatedRequirements(1, paginationState.pageSize);
                }}
                disabled={paginationState.loading}
              >
                <MenuItem value="all">All Levels</MenuItem>
                <MenuItem value="Local">Local (Municipal/County)</MenuItem>
                <MenuItem value="State">State</MenuItem>
                <MenuItem value="Federal">Federal</MenuItem>
                <MenuItem value="null">Unknown</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Show:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={paginationState.pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  disabled={paginationState.loading}
                >
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                per page
              </Typography>
            </Box>
          </Box>

          {paginationState.totalCount === 0 ? (
            <Alert severity="info">
              No approved requirements found matching the selected filters.
            </Alert>
          ) : (
            <>
              <Card elevation={0} sx={{ border: 1, borderColor: 'grey.300' }}>
                <List>
                  {paginatedRequirements.map((requirement, index) => (
                  <React.Fragment key={requirement.id}>
                    <ListItem
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        py: 2,
                      }}
                    >
                      {/* Main Requirement Row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {getComplianceStatusIcon(requirement.compliance_status)}
                        </ListItemIcon>

                        <ListItemText
                          primary={requirement.title}
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} component="div">
                                {requirement.description.slice(0, 120)}...
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                <Chip
                                  label={requirement.category}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                                {requirement.measurement_value && (
                                  <Chip
                                    label={`${requirement.measurement_value}${requirement.unit || ''}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                {requirement.zoning_districts && requirement.zoning_districts.length > 0 && (
                                  <Chip
                                    label={requirement.zoning_districts[0]}
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                  />
                                )}
                                <Chip
                                  label={requirement.compliance_status || 'pending'}
                                  size="small"
                                  color={getComplianceStatusColor(requirement.compliance_status) as any}
                                  variant="filled"
                                />
                                {requirement.compliance_evaluation && (
                                  <Chip
                                    label="AI Analyzed"
                                    size="small"
                                    variant="outlined"
                                    icon={<AutoAwesome />}
                                    sx={{ color: 'primary.main' }}
                                  />
                                )}
                              </Box>

                            </Box>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleExpandToggle(requirement.id)}
                          >
                            {expandedRequirement === requirement.id ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Expanded Details */}
                      <Collapse in={expandedRequirement === requirement.id}>
                        <Box sx={{ mt: 2, pl: 5, pr: 2 }}>
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>Full Description:</strong> {requirement.description}
                          </Typography>

                          {requirement.measurement_value && (
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              <strong>Measurement Requirement:</strong> {requirement.measurement_value} {requirement.unit}
                            </Typography>
                          )}

                          <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>Confidence Score:</strong> {((requirement.confidence_score || 0) * 100).toFixed(0)}%
                          </Typography>

                          {/* AI Analysis Section - Using the new compliance_evaluation field */}
                          {requirement.compliance_evaluation && (
                            <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.100' }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }} component="div">
                                <AutoAwesome fontSize="small" />
                                AI Compliance Analysis
                                {requirement.compliance_evaluation.confidence_score && (
                                  <Chip
                                    label={`${Math.round(requirement.compliance_evaluation.confidence_score * 100)}% confidence`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                  />
                                )}
                              </Typography>

                              <Typography variant="body2" sx={{ mb: 2 }} component="div">
                                <strong>Status:</strong>{' '}
                                <Chip
                                  label={requirement.compliance_status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
                                  size="small"
                                  color={
                                    requirement.compliance_status === 'compliant' ? 'success' :
                                    (requirement.compliance_status === 'non_compliant' || requirement.compliance_status === 'non-compliant') ? 'error' : 'warning'
                                  }
                                  sx={{ ml: 1 }}
                                />
                              </Typography>

                              <Typography variant="body2" sx={{ mb: 2 }}>
                                <strong>AI Explanation:</strong> {requirement.compliance_notes}
                              </Typography>

                              {requirement.compliance_evaluation.measurement_found && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <strong>Measurement Found:</strong> {requirement.compliance_evaluation.measurement_found}
                                </Typography>
                              )}

                              {requirement.compliance_evaluation.measurement_location && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <strong>Location:</strong> {requirement.compliance_evaluation.measurement_location}
                                </Typography>
                              )}

                              {/* Evidence Pages - DEBUG VISIBLE */}
                              {(() => {
                                const hasMappedPages = requirement.compliance_evaluation.evidence_pages_mapped &&
                                                       requirement.compliance_evaluation.evidence_pages_mapped.length > 0;
                                const hasRawPages = requirement.compliance_evaluation.evidence_pages &&
                                                   requirement.compliance_evaluation.evidence_pages.length > 0;

                                console.log('🔍 Evidence Debug for', requirement.id, {
                                  hasMappedPages,
                                  hasRawPages,
                                  mappedCount: requirement.compliance_evaluation.evidence_pages_mapped?.length,
                                  rawCount: requirement.compliance_evaluation.evidence_pages?.length,
                                  mappedPages: requirement.compliance_evaluation.evidence_pages_mapped,
                                  rawPages: requirement.compliance_evaluation.evidence_pages
                                });

                                if (hasMappedPages) {
                                  return (
                                    <EvidencePageChips
                                      mappedPages={requirement.compliance_evaluation.evidence_pages_mapped}
                                      onPageClick={(documentId, pageNumber, documentName) => handlePageClick(requirement, documentId, pageNumber, documentName)}
                                      onViewAllClick={() => handleViewAllClick(requirement)}
                                    />
                                  );
                                } else {
                                  return (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                      <Typography variant="body2">
                                        <strong>Evidence Debug:</strong><br/>
                                        Mapped pages: {requirement.compliance_evaluation.evidence_pages_mapped?.length || 0}<br/>
                                        Raw pages: {requirement.compliance_evaluation.evidence_pages?.length || 0}<br/>
                                        {hasRawPages && !hasMappedPages && (
                                          <>Raw pages exist but not mapped. This may indicate a page mapping issue.</>
                                        )}
                                        {!hasRawPages && (
                                          <>No evidence pages found in AI evaluation.</>
                                        )}
                                      </Typography>
                                    </Alert>
                                  );
                                }
                              })()}
                            </Box>
                          )}

                          {/* Manual Compliance Controls */}
                          <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                            Manual Override:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <Button
                              size="small"
                              variant={requirement.compliance_status === 'compliant' ? 'contained' : 'outlined'}
                              color="success"
                              onClick={() => handleComplianceChange(requirement.id, 'compliant')}
                            >
                              Compliant
                            </Button>
                            <Button
                              size="small"
                              variant={(requirement.compliance_status === 'non_compliant' || requirement.compliance_status === 'non-compliant') ? 'contained' : 'outlined'}
                              color="error"
                              onClick={() => handleComplianceChange(requirement.id, 'non-compliant')}
                            >
                              Non-Compliant
                            </Button>
                            <Button
                              size="small"
                              variant={(requirement.compliance_status === 'not_applicable' || requirement.compliance_status === 'not applicable' || requirement.compliance_status === 'not-applicable') ? 'contained' : 'outlined'}
                              color="info"
                              onClick={() => handleComplianceChange(requirement.id, 'not-applicable')}
                            >
                              Not Applicable
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </ListItem>

                    {index < paginatedRequirements.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Card>

            {/* Pagination */}
            <Pagination
              currentPage={paginationState.currentPage}
              totalPages={paginationState.totalPages}
              onPageChange={handlePageChange}
              loading={paginationState.loading}
              showingStart={paginationState.showingStart}
              showingEnd={paginationState.showingEnd}
              totalCount={paginationState.totalCount}
              itemName="approved requirements"
            />
          </>
          )}

          {/* Action Buttons */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={isGeneratingReport ? <CircularProgress size={16} color="inherit" /> : <Edit />}
              sx={{ px: 4 }}
              onClick={handleGenerateReport}
              disabled={isGeneratingReport || approvedRequirements.length === 0}
            >
              {isGeneratingReport ? 'Generating Report...' : 'Generate Compliance Report'}
            </Button>

            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<CheckCircle />}
              sx={{ px: 4 }}
              disabled={compliancePercentage < 100}
            >
              {compliancePercentage < 100 ?
                `Complete Review (${compliantCount}/${applicableRequirementsCount})` :
                'Submit for Approval'
              }
            </Button>
          </Box>
        </>
      )}

      {/* Evidence Viewer Modals */}
      {selectedRequirement && selectedDocument && (
        <>
          <EvidencePageViewer
            open={evidenceViewerOpen}
            onClose={handleEvidenceViewerClose}
            documentId={selectedDocument.id}
            documentName={selectedDocument.name}
            pageNumber={selectedPage}
            allPages={selectedRequirement.compliance_evaluation?.found_on_pages || []}
            boundingBoxes={selectedRequirement.compliance_evaluation?.bounding_boxes}
            measurementFound={selectedRequirement.compliance_evaluation?.measurement_found}
            measurementLocation={selectedRequirement.compliance_evaluation?.measurement_location}
            requirementTitle={selectedRequirement.title}
            complianceStatus={selectedRequirement.compliance_status}
            onPageChange={handleEvidencePageChange}
          />

          <MultiPageEvidenceViewer
            open={multiPageViewerOpen}
            onClose={handleMultiPageViewerClose}
            documentId={selectedDocument.id}
            documentName={selectedDocument.name}
            evidencePages={selectedRequirement.compliance_evaluation?.found_on_pages || []}
            boundingBoxes={selectedRequirement.compliance_evaluation?.bounding_boxes}
            measurementFound={selectedRequirement.compliance_evaluation?.measurement_found}
            measurementLocation={selectedRequirement.compliance_evaluation?.measurement_location}
            requirementTitle={selectedRequirement.title}
            complianceStatus={selectedRequirement.compliance_status}
            onPageClick={handleMultiPageClick}
          />
        </>
      )}
    </Box>
  );
}